// The three RBP toggles of the filter rail kept consistent, whichever control changed them (the
// rail's checkboxes or a chip). SL1.0 (`www/engine/get_smanager.php:6016-6020`, RULE-025): ticking
// either not-purchased option ticks the core option and un-ticks the other not-purchased option;
// un-ticking the core option un-ticks both. Un-ticking a not-purchased option leaves the core one.
// The server applies the same rule (the core filter is implied, two values are refused); this
// keeps the checkboxes and the request in step. Pure.

import {
  FACET_TOGGLES,
  removeFilterValue,
  toggleFilterValue,
  type FacetKey,
  type ProductFilters,
} from '../../api/facets';

const CORE = FACET_TOGGLES['RBP Core Lines'] ?? { key: 'offerType' as const, value: 'RBP' };

/** One value's change: the value of `key` that was just selected or cleared. */
export interface FilterChange {
  readonly key: FacetKey;
  readonly value: string;
  readonly selected: boolean;
}

/** `next` (the filters after `change`) with the RBP coupling applied. */
export function coupleRbpFilters(next: ProductFilters, change: FilterChange): ProductFilters {
  if (change.key === 'rbpNotPurchased' && change.selected) {
    const core = next[CORE.key] ?? [];
    return {
      ...next,
      rbpNotPurchased: [change.value],
      [CORE.key]: core.includes(CORE.value) ? core : [...core, CORE.value],
    };
  }
  if (change.key === CORE.key && change.value === CORE.value && !change.selected) {
    if (next.rbpNotPurchased === undefined) return next;
    const out: ProductFilters = { ...next };
    delete out.rbpNotPurchased;
    return out;
  }
  return next;
}

/** `toggleFilterValue` with the RBP coupling: the rail's checkbox path. */
export function toggleCoupledFilterValue(
  filters: ProductFilters,
  key: FacetKey,
  value: string,
): ProductFilters {
  const next = toggleFilterValue(filters, key, value);
  return coupleRbpFilters(next, { key, value, selected: next[key]?.includes(value) ?? false });
}

/** `removeFilterValue` with the RBP coupling: the chip's removal path. */
export function removeCoupledFilterValue(
  filters: ProductFilters,
  key: FacetKey,
  value: string,
): ProductFilters {
  return coupleRbpFilters(removeFilterValue(filters, key, value), { key, value, selected: false });
}
